import React from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import TitleButtons from "../../../../../components/TitleButtons";
import HistoricoWidget, {
  HistoricoItem,
} from "../../../../../components/tableHistorico";
import HistoricoPrecosWidget from "../../../../../components/tablePrice";
// ✅ novo widget
import HistoricoPrecosSellWidget from "../../../../../components/tablePriceSell";

// ====================== Tipos de API ======================
type ApiProduto = {
  id: number;
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;
  vendavel: boolean;
  responsavel_registro: number | null;
  departamento: string;
  criado_em: string;
  atualizado_em: string;
  dimensao?: string | null;
  peso?: string | null;
  ocultar?: boolean | null;
};

type ApiMovItem = HistoricoItem;

type MovimentosPorTipo = {
  entrada?: ApiMovItem[];
  saida?: ApiMovItem[];
  perda?: ApiMovItem[];
  venda?: ApiMovItem[];
  consumo?: ApiMovItem[];
  retorno?: ApiMovItem[];
  standby?: ApiMovItem[];
  [k: string]: ApiMovItem[] | undefined;
};

type ApiEstoqueAtual = {
  produto_id: number;
  quantidade_atual: number | null;
  custo_unitario_atual: string | null;
};

// ====== NOVOS TIPOS (preços de venda) ======
type ApiPrecoVendaEntry = {
  id: number;
  data_definicao: string; // ISO
  definido_por_id: number;
  definido_por_nome: string;
  margem_percentual: number; // ex.: 0, 1, 25.5
  preco_venda: number; // BRL
};

type ApiResponse = {
  ok: boolean;
  oculto?: boolean;
  data?: {
    produto: ApiProduto;
    custos: ApiMovItem[];
    movimentos_por_tipo?: MovimentosPorTipo;
    estoque_atual: ApiEstoqueAtual | null;

    // ====== CAMPOS NOVOS ======
    precos_venda?: ApiPrecoVendaEntry[];
    preco_venda?: number | null; // preço atual (conveniência)
  };
  erro?: string;
};

type AuthMe = {
  ok: boolean;
  sessao?: {
    uid?: number;
    email: string;
    nome: string;
    picture?: string;
    ts?: number;
    ttl?: number;
    departamento?: string;
  };
};

// ====================== UI Auxiliar (components) ======================
const NoticeBanner: React.FC<{
  type: "success" | "error" | "notify";
  children: React.ReactNode;
}> = ({ type, children }) => {
  let bg = "#e7f1ff",
    color = "#084298",
    border = "#b6d4fe";
  if (type === "success") {
    bg = "#e6ffed";
    color = "#0f5132";
    border = "#badbcc";
  }
  if (type === "error") {
    bg = "#ffecec";
    color = "#842029";
    border = "#f5c2c7";
  }
  return (
    <div
      role="alert"
      style={{
        margin: "12px 40px 0 40px",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      {children}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div style={{ marginBottom: "24px" }}>
    <div
      style={{
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: "15px",
        marginBottom: "4px",
      }}
    >
      {label}
    </div>
    <div style={{ fontFamily: "Inter", fontWeight: 300, fontSize: "14px" }}>
      {value}
    </div>
  </div>
);

const Card: React.FC<{ title?: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div
    style={{
      margin: "12px 40px 0 40px",
      padding: 16,
      borderRadius: 12,
      border: "1px solid #e6e8ec",
      background: "#fff",
      boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
    }}
  >
    {title && (
      <div
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 15,
          fontWeight: 700,
          color: "#0f172a",
          marginBottom: 12,
        }}
      >
        {title}
      </div>
    )}
    {children}
  </div>
);

const FormField: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label
      style={{
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        fontWeight: 600,
        color: "#334155",
      }}
    >
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: "#64748b" }}>{hint}</div>}
  </div>
);

// ====================== Página ======================
const DetalhesProdutoComercial: React.FC = () => {
  const { codigo: codigoParam } = useParams<{ codigo: string }>();
  const codigo = decodeURIComponent(codigoParam ?? "");

  // ---- States
  const [ocultando, setOcultando] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<ApiResponse["data"] | null>(
    null
  );
  const [isOculto, setIsOculto] = React.useState<boolean>(false);

  // Toggle entre tabelas
  const [activeTab, setActiveTab] = React.useState<
    "mov" | "precosCompra" | "precosVenda"
  >("mov");

  // Ajuste de quantidade
  const [showAdjust, setShowAdjust] = React.useState(false);
  const [qtyInput, setQtyInput] = React.useState<string>("");
  const [adjustLoading, setAdjustLoading] = React.useState(false);
  const [adjustError, setAdjustError] = React.useState<string | null>(null);
  const [adjustSuccess, setAdjustSuccess] = React.useState<string | null>(null);

  // Ajuste de margem de venda
  const [showMargin, setShowMargin] = React.useState(false);
  const [marginInput, setMarginInput] = React.useState<string>(""); // %
  const [priceInput, setPriceInput] = React.useState<string>(""); // preço de venda
  const [marginPosting, setMarginPosting] = React.useState(false);
  const [marginError, setMarginError] = React.useState<string | null>(null);
  const [marginSuccess, setMarginSuccess] = React.useState<string | null>(null);

  // Vendável toggle
  const [vendavelPosting, setVendavelPosting] = React.useState(false);
  const [vendavelMsg, setVendavelMsg] = React.useState<string | null>(null);
  const [vendavelErr, setVendavelErr] = React.useState<string | null>(null);

  // UID já autenticado
  const [authUid, setAuthUid] = React.useState<number | null>(null);

  // responsividade simples (2 colunas -> 1 em telas estreitas)
  const [isNarrow, setIsNarrow] = React.useState<boolean>(
    () => window.innerWidth < 920
  );
  React.useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 920);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const navigate = useNavigate();

  // ---- formatadores + parsers
  const formatCurrency = (v: string | number | null | undefined) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(n);
  };

  const formatNumber = (v: number, maxFrac = 2) =>
    new Intl.NumberFormat("pt-BR", { maximumFractionDigits: maxFrac }).format(
      v
    );

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return "—";
    const m = iso.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
    );
    if (!m) return iso || "—";
    const [, ano, mes, dia, hora, minuto] = m;
    return `${dia}/${mes}/${ano} - ${hora}:${minuto}`;
  };

  // Troque seus parseLocaleNumber por este:
  const smartParseNumber = (val: unknown): number => {
    if (val == null) return NaN;
    if (typeof val === "number") return val;
    const s = String(val).trim();

    // tem ponto ou vírgula?
    if (/[.,]/.test(s)) {
      const lastComma = s.lastIndexOf(",");
      const lastDot = s.lastIndexOf(".");
      if (lastComma > lastDot) {
        // vírgula é decimal, ponto é milhar: 14.276,28 -> 14276.28
        return Number(s.replace(/\./g, "").replace(",", "."));
      }
      if (lastDot > lastComma) {
        // ponto é decimal, vírgula é milhar: 14,276.28 -> 14276.28
        return Number(s.replace(/,/g, ""));
      }
    }
    // só vírgula: "14276,28" -> 14276.28
    if (s.includes(",")) return Number(s.replace(",", "."));
    // só ponto ou sem separadores
    return Number(s);
  };

  // ---- /auth/me
  React.useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const res = await fetch("https://api.eva-googlemarine.com/auth/me", {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`Falha em /auth/me (HTTP ${res.status})`);
        const json: AuthMe = await res.json();
        const rawUid = json?.sessao?.uid;
        const nextUid: number | null =
          typeof rawUid === "number" && Number.isInteger(rawUid)
            ? rawUid
            : null;

        if (!disposed) {
          setAuthUid(nextUid);
          if (nextUid !== null) {
            try {
              (window as any).__EVA_AUTH_UID__ = nextUid;
              localStorage.setItem("eva_auth_uid", String(nextUid));
            } catch {}
          }
        }
      } catch {
        if (!disposed) setAuthUid(null);
      }
    })();
    return () => {
      disposed = true;
    };
  }, []);

  // ---- (re)carregar o produto ----
  const fetchProduto = async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `https://api.eva-googlemarine.com/departamentos/diretoria/produtos/${encodeURIComponent(
          codigo
        )}`,
        { signal }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: ApiResponse = await res.json();
      if (!json.ok || !json.data)
        throw new Error(json.erro || "Resposta inválida da API");
      setPayload(json.data);
      setIsOculto(Boolean(json.oculto) || Boolean(json.data.produto?.ocultar));
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setError(e?.message || "Falha ao buscar produto");
        setPayload(null);
        setIsOculto(false);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!codigo) return;
    const abort = new AbortController();
    fetchProduto(abort.signal);
    return () => abort.abort();
  }, [codigo]);

  // ========= Derivados (sem hooks) =========

  // Histórico completo ordenado
  const movimentosOrdenados = (() => {
    const mov = payload?.movimentos_por_tipo;
    if (!mov) return [] as ApiMovItem[];
    type ApiMovRaw = ApiMovItem & { tipo_full?: string | null };
    const all: ApiMovRaw[] = Object.values(mov).flatMap(
      (arr) => arr ?? []
    ) as ApiMovRaw[];
    const withDisplayTipo: ApiMovItem[] = all.map((i) => {
      const isAjuste =
        String((i as any).tipo_full || "").toLowerCase() === "ajuste";
      return isAjuste ? { ...i, tipo: "Ajuste" } : i;
    });
    return withDisplayTipo.sort((a, b) => {
      const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
      const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;
      return tb - ta;
    });
  })();

  // Custo unitário (string) e número
  const custoUnitario = (() => {
    const atual = payload?.estoque_atual?.custo_unitario_atual;
    const historicoMaisRecente = payload?.custos?.[0]?.custo_unitario;
    return formatCurrency(atual ?? historicoMaisRecente ?? null);
  })();

  const custoUnitarioNumber = (() => {
    const raw =
      payload?.estoque_atual?.custo_unitario_atual ??
      payload?.custos?.[0]?.custo_unitario;
    const n = smartParseNumber(raw);
    return Number.isFinite(n) ? n : NaN;
  })();

  // inputs
  const quantidadeEstoque = (() => {
    const q = payload?.estoque_atual?.quantidade_atual;
    return q == null ? "—" : String(q);
  })();

  // Cálculos margem/preço
  const marginPctNumber = smartParseNumber(marginInput);
  const priceNumber = smartParseNumber(priceInput);

  const priceFromMargin = (() => {
    if (
      !Number.isFinite(custoUnitarioNumber) ||
      !Number.isFinite(marginPctNumber)
    )
      return NaN;
    if (marginInput.trim() === "") return NaN;
    if (marginPctNumber >= 100) return NaN;
    return custoUnitarioNumber / (1 - marginPctNumber / 100);
  })();

  const marginFromPrice = (() => {
    if (
      !Number.isFinite(custoUnitarioNumber) ||
      !Number.isFinite(priceNumber) ||
      priceInput.trim() === ""
    )
      return NaN;
    if (priceNumber <= 0) return NaN;
    return ((priceNumber - custoUnitarioNumber) / priceNumber) * 100;
  })();

  // ===== Derivados simples (sem hooks)
  const nome = payload?.produto?.nome ?? "—";
  const ncm = payload?.produto?.ncm ?? "—";
  const classificacao = payload?.produto?.classificacao ?? "—";
  const posicao = payload?.produto?.posicao ?? "—";
  const dimensao = payload?.produto?.dimensao ?? "—";
  const peso = payload?.produto?.peso ? `${payload.produto.peso}` : "—";

  const vendavelAtual = !!payload?.produto?.vendavel;

  // ===== Preços de venda (derivados) =====
  const precosVendaArr: ApiPrecoVendaEntry[] = Array.isArray(
    payload?.precos_venda
  )
    ? [...payload!.precos_venda!]
    : [];

  // Ordenar por data_definicao desc (mais recente primeiro)
  precosVendaArr.sort((a, b) => {
    const ta = Date.parse(a.data_definicao || "");
    const tb = Date.parse(b.data_definicao || "");
    return tb - ta;
  });

  const precoVendaAtualNum: number | null = (() => {
    const n = smartParseNumber(
      payload?.preco_venda ?? precosVendaArr[0]?.preco_venda ?? null
    );
    return Number.isFinite(n) ? n : null;
  })();

  const precoVendaUltimoNum: number | null =
    precosVendaArr.length >= 2 && Number.isFinite(precosVendaArr[1].preco_venda)
      ? precosVendaArr[1].preco_venda
      : null;

  const precoVendaAtualFmt =
    precoVendaAtualNum != null ? formatCurrency(precoVendaAtualNum) : "—";
  const precoVendaUltimoFmt =
    precoVendaUltimoNum != null ? formatCurrency(precoVendaUltimoNum) : "—";

  const margemAtualPct: number | null =
    precosVendaArr[0]?.margem_percentual != null
      ? Number(precosVendaArr[0].margem_percentual)
      : null;

  // Itens para o widget de venda (tipo sempre "Ajuste")
  type SellHistItem = {
    id: number;
    produto_id: number;
    movimentacao_id?: number;
    tipo?: string | null;
    custo_unitario: string | number | null; // aqui usamos para guardar o preço de venda
    quantidade: number | null;
    data_movimentacao: string | null;
    responsavel_id: number | null;
    responsavel_nome?: string | null;
    margem_percentual?: number | null; // extra
  };

  const historicoPrecosVenda: SellHistItem[] = precosVendaArr.map((p) => ({
    id: p.id,
    produto_id: payload?.produto?.id ?? 0,
    movimentacao_id: undefined,
    tipo: "Ajuste",
    custo_unitario: p.preco_venda,
    quantidade: null,
    data_movimentacao: p.data_definicao,
    responsavel_id: p.definido_por_id,
    responsavel_nome: p.definido_por_nome ?? null,
    margem_percentual: p.margem_percentual ?? null,
  }));

  // ✅ nenhum Hook depois daqui
  if (!codigo) return <Navigate to="/suprimentos/produtos" replace />;

  // ---- ações
  const handleOcultar = async () => {
    if (!codigo || ocultando) return;
    const confirma = window.confirm(`Ocultar o produto ${codigo}?`);
    if (!confirma) return;
    try {
      setOcultando(true);
      const r1 = await fetch(
        "https://api.eva-googlemarine.com/ocultar-produto",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ codigo }),
        }
      );
      const t1 = await r1.text().catch(() => "");
      let j1: any = null;
      try {
        j1 = t1 ? JSON.parse(t1) : null;
      } catch {}
      if (!r1.ok || j1?.ok === false)
        throw new Error(j1?.erro || `Falha ao ocultar (HTTP ${r1.status})`);

      const r2 = await fetch(
        "https://api.eva-googlemarine.com/departamentos/suprimentos/produtos/invalidate",
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      );
      if (!r2.ok) {
        const t2 = await r2.text().catch(() => "");
        throw new Error(
          `Falha ao invalidar cache (HTTP ${r2.status}) ${t2 || ""}`.trim()
        );
      }
      navigate("/suprimentos/produtos", { replace: true });
    } catch (e: any) {
      window.alert(e?.message || "Falha ao ocultar produto.");
    } finally {
      setOcultando(false);
    }
  };

  // -------- Toggle Vendável --------
  const handleToggleVendavel = async () => {
    if (!payload?.produto?.codigo) return;
    setVendavelErr(null);
    setVendavelMsg(null);

    const nextVendavel = !vendavelAtual;
    const ncmAtual = payload?.produto?.ncm || "";

    try {
      setVendavelPosting(true);
      const resp = await fetch(
        "https://api.eva-googlemarine.com/produto-vendavel",

        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            codigo: payload.produto.codigo,
          }),
        }
      );

      const txt = await resp.text().catch(() => "");
      let j: any = null;
      try {
        j = txt ? JSON.parse(txt) : null;
      } catch {}

      if (!resp.ok || j?.ok === false) {
        const msg =
          j?.erro ||
          `Falha ao atualizar vendável (HTTP ${resp.status}) ${
            txt || ""
          }`.trim();
        throw new Error(msg);
      }

      // Atualiza localmente para feedback instantâneo
      setPayload((prev) =>
        prev
          ? {
              ...prev,
              produto: {
                ...prev.produto,
                ncm: ncmAtual,
                vendavel: nextVendavel,
              },
            }
          : prev
      );

      setVendavelMsg(
        nextVendavel
          ? "Produto definido como Vendável."
          : "Produto definido como Não Vendável."
      );

      // Recarrega do backend para garantir consistência
      await fetchProduto();
    } catch (e: any) {
      setVendavelErr(e?.message || "Falha ao atualizar status de venda.");
    } finally {
      setVendavelPosting(false);
    }
  };

  // -------- Ajuste de quantidade
  const handleOpenAdjust = () => {
    setAdjustError(null);
    setAdjustSuccess(null);
    setQtyInput("");
    setShowAdjust(true);
  };
  const handleCancelAdjust = () => {
    if (adjustLoading) return;
    setShowAdjust(false);
    setAdjustError(null);
    setAdjustSuccess(null);
  };
  const handleConfirmAdjust = async () => {
    if (!codigo || adjustLoading) return;
    setAdjustError(null);
    setAdjustSuccess(null);

    if (!Number.isInteger(authUid as any)) {
      setAdjustError(
        "Sessão não disponível. Recarregue a página ou faça login novamente."
      );
      return;
    }

    const qNum = Number(qtyInput);
    if (!Number.isFinite(qNum) || !Number.isInteger(qNum)) {
      setAdjustError("Digite um número inteiro válido para a quantidade.");
      return;
    }

    try {
      setAdjustLoading(true);
      const postRes = await fetch(
        "https://api.eva-googlemarine.com/ajustar-produto",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            codigo,
            responsavel_id: authUid,
            quantidade: qNum,
          }),
        }
      );

      const postTxt = await postRes.text().catch(() => "");
      let postJson: any = null;
      try {
        postJson = postTxt ? JSON.parse(postTxt) : null;
      } catch {}
      if (!postRes.ok || postJson?.ok === false) {
        const msg =
          postJson?.erro ||
          `Falha ao ajustar (HTTP ${postRes.status}) ${postTxt || ""}`.trim();
        throw new Error(msg);
      }

      setAdjustSuccess("Quantidade ajustada com sucesso.");
      await fetchProduto();
      setShowAdjust(false);
    } catch (e: any) {
      setAdjustError(e?.message || "Falha ao ajustar quantidade.");
    } finally {
      setAdjustLoading(false);
    }
  };

  // -------- Ajuste de margem de venda (mantido)
  const handleOpenMargin = () => {
    setMarginError(null);
    setMarginSuccess(null);
    setMarginPosting(false);
    setShowMargin(true);
  };
  const handleCancelMargin = () => {
    if (marginPosting) return;
    setShowMargin(false);
    setMarginError(null);
    setMarginSuccess(null);
  };

  const handleConfirmMargin = async () => {
    if (!payload?.produto?.id) {
      setMarginError("Produto inválido.");
      return;
    }
    if (!Number.isInteger(authUid as any)) {
      setMarginError(
        "Sessão não disponível. Recarregue a página ou faça login novamente."
      );
      return;
    }

    let margemPercentual: number | null = null;

    if (marginInput.trim() !== "" && Number.isFinite(marginPctNumber)) {
      if (marginPctNumber < 0 || marginPctNumber >= 100) {
        setMarginError("Informe uma margem em % entre 0 e 100.");
        return;
      }
      margemPercentual = marginPctNumber;
    } else if (priceInput.trim() !== "" && Number.isFinite(priceNumber)) {
      if (priceNumber <= 0) {
        setMarginError("Informe um preço de venda maior que zero.");
        return;
      }
      const m = marginFromPrice;
      if (!Number.isFinite(m) || m < 0 || m >= 100) {
        setMarginError(
          "Preço informado gera margem fora do intervalo (0-100%)."
        );
        return;
      }
      margemPercentual = m;
    } else {
      setMarginError("Preencha Margem (%) ou Preço de Venda.");
      return;
    }

    try {
      setMarginPosting(true);
      setMarginError(null);
      setMarginSuccess(null);

      const resp = await fetch(
        "https://api.eva-googlemarine.com/departamentos/diretoria/ajustar-margem",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            produto_id: payload.produto.id,
            definido_por_id: authUid,
            margem_percentual: Number(margemPercentual.toFixed(4)),
          }),
        }
      );

      const txt = await resp.text().catch(() => "");
      let j: any = null;
      try {
        j = txt ? JSON.parse(txt) : null;
      } catch {}
      if (!resp.ok || j?.ok === false) {
        const msg =
          j?.erro ||
          `Falha ao ajustar margem (HTTP ${resp.status}) ${txt || ""}`.trim();
        throw new Error(msg);
      }

      setMarginSuccess("Margem registrada com sucesso.");
      await fetchProduto();
    } catch (e: any) {
      setMarginError(e?.message || "Falha ao ajustar margem.");
    } finally {
      setMarginPosting(false);
    }
  };

  // ====================== Estilos base ======================
  const toggleWrapStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    padding: "0 40px",
    margin: "8px 0 12px 0",
    alignItems: "center",
    flexWrap: "wrap",
  };
  const baseBtn: React.CSSProperties = {
    padding: "8px 14px",
    borderRadius: 4,
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #ccc",
    background: "#124280",
    color: "#fff",
    outline: "none",
    userSelect: "none",
    transition:
      "transform 0.02s ease-in-out, box-shadow 0.1s ease-in-out, opacity 0.12s",
  };
  const btnActive: React.CSSProperties = {
    ...baseBtn,
    boxShadow: "0 0 0 1px rgba(18,66,128,0.15)",
    opacity: 1,
  };
  const btnInactive: React.CSSProperties = { ...baseBtn, opacity: 0.7 };

  // painel de ajuste de quantidade
  const adjustPanelWrap: React.CSSProperties = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    padding: "0 40px",
    marginTop: 8,
  };
  const adjustInputStyle: React.CSSProperties = {
    width: 140,
    height: 34,
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: "0 10px",
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
  };
  const adjustBtnConfirm: React.CSSProperties = {
    ...baseBtn,
    background: "#114280",
    borderColor: "#114280",
  };
  const adjustBtnCancel: React.CSSProperties = {
    ...baseBtn,
    background: "#6c757d",
    borderColor: "#6c757d",
  };

  // estilo do "chip" de status vendável (arredondamento menor)
  const sellStatusChip: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 2, // diminuído (antes era bem arredondado)

    background: vendavelAtual ? "#e6ffed" : "#ffecec",
    color: vendavelAtual ? "#0f5132" : "#842029",
    border: `1px solid ${vendavelAtual ? "#badbcc" : "#f5c2c7"}`,
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
  };

  // botão contornado azul
  const outlineBlueBtn: React.CSSProperties = {
    padding: "5px 15px",
    borderRadius: 2,
    border: vendavelAtual ? "1.5px solid #801212FF" : "1.5px solid #128021FF",
    background: "#fff",
    color: vendavelAtual ? "#801212FF" : "#03491BFF",
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  };

  return (
    <div>
      <TitleButtons
        outerMarginLeft={40}
        outerMarginRight={20}
        title={`Diretoria - Detalhes do Produto / ${codigo}`}
        buttonMarginRight="0px"
      />

      {/* ===== Linha de status + botão Toggle Vendável (empilhados) ===== */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 8,
          padding: "0 40px",
          margin: "12px 0 20px 0",
        }}
      >
        {/* botão contornado azul */}

        {/* mensagens de status */}
        {(vendavelPosting || vendavelMsg || vendavelErr) && (
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              fontFamily: "Inter, sans-serif",
            }}
          >
            {vendavelPosting && (
              <span style={{ color: "#64748b" }}>Atualizando…</span>
            )}
            {vendavelMsg && (
              <span style={{ color: "#0f5132" }}>{vendavelMsg}</span>
            )}
            {vendavelErr && (
              <span style={{ color: "#b02a37" }}>{vendavelErr}</span>
            )}
          </div>
        )}
      </div>

      {isOculto && (
        <NoticeBanner type="notify">
          Este produto está marcado como <strong>oculto</strong>. Ele não
          aparecerá nas listagens padrão.
        </NoticeBanner>
      )}

      {/* Painel de Ajuste de Margem de Venda - Card responsivo */}
      {showMargin && (
        <Card title="Ajuste de Margem de Venda">
          {!Number.isFinite(custoUnitarioNumber) ? (
            <NoticeBanner type="error">
              Custo unitário indisponível — não é possível calcular margem.
            </NoticeBanner>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            {/* Coluna esquerda: entrada de dados */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 12,
              }}
            >
              <FormField label="Custo unitário atual">
                <div
                  style={{
                    height: 38,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    background: "#f8fafc",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    color: "#0f172a",
                  }}
                  aria-live="polite"
                >
                  {custoUnitario}
                </div>
              </FormField>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <FormField
                  label="Margem (%)"
                  hint="margem = (preço − custo) ÷ preço × 100"
                >
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 25"
                    style={{
                      height: 38,
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "0 12px",
                      fontFamily: "Inter, sans-serif",
                      fontSize: 13,
                    }}
                    value={marginInput}
                    onChange={(e) => setMarginInput(e.target.value)}
                    disabled={
                      !Number.isFinite(custoUnitarioNumber) || marginPosting
                    }
                  />
                </FormField>

                <FormField
                  label="Preço de venda (R$)"
                  hint="Preencha margem OU preço"
                >
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 199,90"
                    style={{
                      height: 38,
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "0 12px",
                      fontFamily: "Inter, sans-serif",
                      fontSize: 13,
                    }}
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    disabled={
                      !Number.isFinite(custoUnitarioNumber) || marginPosting
                    }
                  />
                </FormField>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={handleConfirmMargin}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #114280",
                    background: "#fff",
                    color: "#114280",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  disabled={
                    marginPosting || !Number.isFinite(custoUnitarioNumber)
                  }
                  aria-busy={marginPosting}
                >
                  {marginPosting
                    ? "Registrando…"
                    : "Confirmar ajuste de margem"}
                </button>

                <button
                  type="button"
                  onClick={handleCancelMargin}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    background: "#f1f5f9",
                    color: "#0f172a",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  disabled={marginPosting}
                >
                  Cancelar
                </button>

                {marginError && (
                  <span
                    style={{ color: "#b02a37", marginLeft: 8, fontSize: 13 }}
                  >
                    {marginError}
                  </span>
                )}
                {marginSuccess && (
                  <span
                    style={{ color: "#0f5132", marginLeft: 8, fontSize: 13 }}
                  >
                    {marginSuccess}
                  </span>
                )}
              </div>
            </div>

            {/* Coluna direita: resumo */}
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 16,
                background: "#f8fafc",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0f172a",
                }}
              >
                Resumo
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13,
                  color: "#0f172a",
                }}
              >
                <div style={{ opacity: 0.7 }}>Custo</div>
                <div style={{ textAlign: "right" }}>{custoUnitario}</div>

                <div style={{ opacity: 0.7 }}>Margem (%)</div>
                <div style={{ textAlign: "right" }}>
                  {marginInput.trim() !== "" && Number.isFinite(marginPctNumber)
                    ? `${marginPctNumber.toFixed(2)}%`
                    : Number.isFinite(marginFromPrice)
                    ? `${marginFromPrice.toFixed(2)}%`
                    : "—"}
                </div>

                <div style={{ opacity: 0.7 }}>Preço de venda</div>
                <div style={{ textAlign: "right", fontWeight: 700 }}>
                  {marginInput.trim() !== "" && Number.isFinite(priceFromMargin)
                    ? formatCurrency(priceFromMargin)
                    : Number.isFinite(priceNumber)
                    ? formatCurrency(priceNumber)
                    : "—"}
                </div>
              </div>

              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "#64748b",
                  lineHeight: 1.4,
                }}
              >
                Dica: com margem{" "}
                <strong>
                  {marginInput.trim() !== "" && Number.isFinite(marginPctNumber)
                    ? `${formatNumber(marginPctNumber, 2)}%`
                    : Number.isFinite(marginFromPrice)
                    ? `${formatNumber(marginFromPrice, 2)}%`
                    : "—"}
                </strong>
                , o preço sugerido é{" "}
                <strong>
                  {marginInput.trim() !== "" && Number.isFinite(priceFromMargin)
                    ? formatCurrency(priceFromMargin)
                    : Number.isFinite(priceNumber)
                    ? formatCurrency(priceNumber)
                    : "—"}
                </strong>
                .
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Painel de Ajuste de Quantidade */}
      {showAdjust && (
        <div style={adjustPanelWrap}>
          <label style={{ fontFamily: "Inter, sans-serif", fontSize: 13 }}>
            Quantidade:&nbsp;
            <input
              type="number"
              step="1"
              inputMode="numeric"
              style={adjustInputStyle}
              value={qtyInput}
              onChange={(e) => setQtyInput(e.target.value)}
              placeholder="Ex.: 42"
              disabled={adjustLoading}
            />
          </label>
          <button
            type="button"
            onClick={handleConfirmAdjust}
            style={adjustBtnConfirm}
            disabled={adjustLoading}
            aria-busy={adjustLoading}
          >
            {adjustLoading ? "Ajustando..." : "Confirmar"}
          </button>
          <button
            type="button"
            onClick={handleCancelAdjust}
            style={adjustBtnCancel}
            disabled={adjustLoading}
          >
            Cancelar
          </button>
          {adjustError && (
            <span style={{ color: "#b02a37", marginLeft: 8, fontSize: 13 }}>
              {adjustError}
            </span>
          )}
          {adjustSuccess && (
            <span style={{ color: "#0f5132", marginLeft: 8, fontSize: 13 }}>
              {adjustSuccess}
            </span>
          )}
        </div>
      )}

      {/* bloco 2 colunas */}
      <div
        style={{
          display: "flex",
          padding: "40px",
          gap: "100px",
          marginLeft: "0px",
        }}
      >
        <div style={{ flex: 1 }}>
          <Field
            label="Nome do Produto"
            value={loading ? "Carregando..." : error ? "—" : nome}
          />
          <Field label="Código do Produto" value={codigo} />
          <Field
            label="Código NCM do Produto"
            value={loading ? "Carregando..." : error ? "—" : ncm}
          />
          <Field
            label="Classificação"
            value={loading ? "Carregando..." : error ? "—" : classificacao}
          />
          <Field
            label="Dimensão"
            value={loading ? "Carregando..." : error ? "—" : dimensao}
          />
          <Field
            label="Peso"
            value={loading ? "Carregando..." : error ? "—" : peso}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Field
            label="Posição do Produto"
            value={loading ? "Carregando..." : error ? "—" : posicao}
          />
          <Field
            label="Custo Unitário (Atual / Último)"
            value={loading ? "Carregando..." : error ? "—" : custoUnitario}
          />

          {/* ====== NOVO BLOCO: Preço de venda (Atual / Último) + Margem ====== */}
          <Field
            label="Preço de venda (Atual / Último)"
            value={
              loading
                ? "Carregando..."
                : error
                ? "—"
                : `${precoVendaAtualFmt}${
                    precoVendaUltimoNum != null
                      ? ` / ${precoVendaUltimoFmt}`
                      : ""
                  }`
            }
          />
          {!loading && !error && (
            <div
              style={{
                marginTop: "-18px",
                marginBottom: "6px",
                fontFamily: "Inter",
                fontSize: 12,
                color: "#64748b",
                paddingLeft: "0px",
              }}
            >
              Margem atual:{" "}
              <strong>
                {margemAtualPct != null && Number.isFinite(margemAtualPct)
                  ? `${margemAtualPct.toFixed(2)}%`
                  : "—"}
              </strong>
            </div>
          )}

          <Field
            label="Quantidade no Estoque"
            value={loading ? "Carregando..." : error ? "—" : quantidadeEstoque}
          />
          {error && (
            <div
              style={{
                marginTop: "24px",
                color: "#c0362c",
                fontFamily: "Inter",
                fontSize: 13,
              }}
            >
              Erro ao carregar dados do produto: {error}
            </div>
          )}
        </div>
      </div>

      {/* Toggle entre tabelas */}
      <div style={toggleWrapStyle} role="group" aria-label="Alternar tabela">
        <button
          type="button"
          onClick={() => setActiveTab("mov")}
          style={activeTab === "mov" ? btnActive : btnInactive}
          aria-pressed={activeTab === "mov"}
        >
          Histórico de Movimentações
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("precosCompra")}
          style={activeTab === "precosCompra" ? btnActive : btnInactive}
          aria-pressed={activeTab === "precosCompra"}
        >
          Histórico de Preços de Compra
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("precosVenda")}
          style={activeTab === "precosVenda" ? btnActive : btnInactive}
          aria-pressed={activeTab === "precosVenda"}
        >
          Histórico de Preços de Venda
        </button>
      </div>

      {/* Tabelas (uma por vez) */}
      <div style={{ padding: "0 40px", marginLeft: "0px", marginTop: "4px" }}>
        {activeTab === "mov" ? (
          loading ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
              Carregando histórico…
            </div>
          ) : movimentosOrdenados.length === 0 ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
              Sem movimentações para este produto.
            </div>
          ) : (
            <HistoricoWidget
              items={movimentosOrdenados}
              loading={loading}
              error={error}
              formatCurrency={formatCurrency}
              formatDateTime={formatDateTime}
            />
          )
        ) : activeTab === "precosCompra" ? (
          <HistoricoPrecosWidget
            items={payload?.custos ?? []}
            loading={loading}
            error={error}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
          />
        ) : (
          <HistoricoPrecosSellWidget
            items={historicoPrecosVenda}
            loading={loading}
            error={error}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
          />
        )}
      </div>
    </div>
  );
};

export default DetalhesProdutoComercial;
